import React from "react";

import not from "ramda/src/not";
import map from "ramda/src/map";
import set from "ramda/src/set";
import lensProp from "ramda/src/lensProp";
import identity from "ramda/src/identity";
import prop from "ramda/src/prop";
import ifElse from "ramda/src/ifElse";
import compose from "ramda/src/compose";

import Api from "../../services/Api";
import useAuth from "../../routes/login/AuthContext";
import {
  useNetworkContext,
  useSelectedNetworkContext,
} from "../../contexts/NetworkContext/context";
import { ReadOnlyWrapper } from "../ReadOnlyWrapper";
import { useSnackbar } from "../../hooks/useSnackbar";
import { IExtendedL3 } from "../../services/NetworkApi";

import { Switch } from "../Switch";

interface IProps {
  l2Id: number;
  l3Id: number;
  isDisabled?: boolean;
  defaultValue: boolean;
  updateData: React.Dispatch<React.SetStateAction<IExtendedL3[]>>;
}

const getLabel = (isVisible: boolean) =>
  isVisible ? "Visible" : "Not Visible";

export const VisibilitySwitch: React.FC<IProps> = (props) => {
  const { getAuthToken, isSuperAdminRead } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(false);
  const [value, setValue] = React.useState(props.defaultValue);
  const { updateUnitTypeValues } = useNetworkContext();
  const { selectedNetworkId } = useSelectedNetworkContext();

  React.useEffect(() => {
    setValue(props.defaultValue);
  }, [props.defaultValue]);

  const updateLocalNetworkData = React.useCallback(
    (renewalRenewalFindingAllowed: boolean) => () => {
      updateUnitTypeValues(selectedNetworkId, props.l2Id, props.l3Id, {
        renewalRenewalFindingAllowed,
      });
    },
    [selectedNetworkId, props.l2Id, updateUnitTypeValues]
  );

  const updateData = (renewalRenewalFindingAllowed: boolean) => () =>
    props.updateData(
      map(
        ifElse(
          (l3) => l3.l3Id === props.l3Id && l3.l2Id === props.l2Id,
          set(
            lensProp("renewalRenewalFindingAllowed"),
            renewalRenewalFindingAllowed
          ),
          identity
        )
      )
    );

  const stopLoading = () => setIsLoading(false);

  const handleError = () =>
    showErrorSnackbar("An error occurred. Please try again");

  const onComplete = () => showSuccessSnackbar("Saved Changes");

  const handleSuccess = (renewalRenewalFindingAllowed: boolean) =>
    ifElse(
      prop("success"),
      compose(
        onComplete,
        updateLocalNetworkData(renewalRenewalFindingAllowed),
        updateData(renewalRenewalFindingAllowed)
      ),
      handleError
    );

  const handleChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    allowRenewalRenewalFinding: boolean
  ) => {
    const body = {
      subnetworkCommands: [],
      unitTypeCommands: [
        {
          l1Id: selectedNetworkId,
          l2Id: props.l2Id,
          l3Id: props.l3Id,
          allowRenewalRenewalFinding,
        },
      ],
    };

    setIsLoading(true);
    setValue(not);

    Api.setMathingRules(selectedNetworkId, body, await getAuthToken())
      .then(handleSuccess(allowRenewalRenewalFinding))
      .catch(handleError)
      .finally(stopLoading);
  };

  return (
    <ReadOnlyWrapper>
      <Switch
        isDisabled={isLoading || props.isDisabled || isSuperAdminRead()}
        label={getLabel(value)}
        switchProps={{ checked: value, onChange: handleChange }}
      />
    </ReadOnlyWrapper>
  );
};
